import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  Output,
  EventEmitter,
} from '@angular/core';
import { Observable, Subject, catchError, map, of, switchMap, takeUntil } from 'rxjs';

import { TabDetailsSections } from '../coaching.constants';
import { Section } from '@app/models';
import { Note } from '@app/models/note.model';
import { DialogEventService } from '@app/shared/components/dialog-event/dialog-event.service';
import { EventService } from '@app/services/event.service';
import { addSpaceAfterComma } from '@app/shared/helpers/string.helper';
import {
  formatDateWithAt,
  convertFromDatabaseToDatePickerFormat,
} from '@app/shared/helpers/date.helper';
import { getBackgroundClass, getInitials } from '@app/shared/helpers/functions.helper';
import { MatDialog } from '@angular/material/dialog';
import { DialogCoachingSamplesComponent } from '@app/shared/components/dialog-coaching-samples/dialog-coaching-samples.component';
import { DialogEventComponent } from '@app/shared/components/dialog-event/dialog-event.component';
import { calculateDialogWidth } from '@app/shared/helpers/functions.helper';
import { InterventionService } from '@app/services/intervention.service';
import { ToasterService } from '@app/services/toaster.service';
import { FlyoutType } from '@app/models/fly-out.model';
import { FlyoutService } from '@app/services/flyout.service';
import { StatusType, CoachingStatusType } from '@app/models/status.model';
import { DialogConfirmComponent } from '../../dialog-confirm/dialog-confirm.component';
import { PermissionService } from '@app/services/permission.service';
import { environment } from '@env/environment';
import { MOBILE_WIDTH } from '@app/shared/config.constants';
import { MediaService } from '@app/services/media.service';
import { UserManagementService } from '@app/services/user-management.service';
import { AssigneeUsersType } from '@app/models/general.model';

@Component({
  selector: 'app-tab-section-details',
  templateUrl: './tab-section-details.component.html',
  styleUrls: ['./tab-section-details.component.scss'],
})
export class TabSectionDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() showMenu!: boolean;
  @Input() interventionId!: string;
  @Input() driverId!: string;
  @Input() dataRow!: any;

  sectionSelected: string = 'coaching-plan';
  coachingScriptContents!: string;
  coachingScriptOriginalContents!: string;
  interventionDetail!: any;
  linkedEventList: any[] = [];
  linkedEventColumns: string[] = [];
  isLoading: boolean = false;
  userId!: string;
  maxAuditHistory: number = 5;

  userName: string = '';
  avatarUrl: string = '';
  noteContents: string = '';
  notes: Note[] = [];
  entityType: string = 'intervention';
  statuses = [
    {
      value: StatusType.OPEN.toLowerCase(),
      viewValue: StatusType.OPEN,
      icon: 'fa-solid fa-circle-notch',
    },
    {
      value: StatusType.ASSIGNED.toLowerCase(),
      viewValue: StatusType.ASSIGNED,
      icon: 'fa-solid fa-user-circle icon-second-blue',
    },
    {
      value: StatusType.CLOSED.toLowerCase(),
      viewValue: StatusType.CLOSED,
      icon: 'fa-solid fa-circle-xmark icon-red icon-default-size',
    },
    {
      value: StatusType.NON_EVENT.toLowerCase(),
      viewValue: StatusType.NON_EVENT,
      icon: 'fa-solid fa-triangle-exclamation icon-red icon-default-size',
      disabled: false,
    },
  ];
  selectedRow: string | null = null;
  showSaveButton: boolean = false;
  changesAmount: number = 0;
  coachingStatistics: any[] = [];
  coachingExamples: any[] = [];

  selectedOrganization: any = {};

  addSpaceAfterComma = addSpaceAfterComma;
  formatDateWithAt = formatDateWithAt;
  convertFromDatabaseToDatePickerFormat = convertFromDatabaseToDatePickerFormat;
  getBackgroundClass = getBackgroundClass;
  getInitials = getInitials;

  // Audit history properties
  interventionAuditHistory: any[] = [];
  displayedAuditHistory: any[] = [];
  showAllHistory: boolean = false;
  isLoadingAudit: boolean = false;

  readonly sections: Section[] = TabDetailsSections;
  private destroy$ = new Subject<void>();

  @ViewChild('scrollContainer') scrollContainer!: ElementRef;

  assigneeImages: { [userId: string]: string | null } = {};

  users: any[] = [];

  @Output() saveComplete: EventEmitter<void> = new EventEmitter<void>();
  @Output() saveAndCompleteClicked = new EventEmitter<void>();

  constructor(
    private dialogEventService: DialogEventService,
    private eventService: EventService,
    private dialog: MatDialog,
    private interventionService: InterventionService,
    private toasterService: ToasterService,
    private flyoutService: FlyoutService,
    private permissionService: PermissionService,
    private mediaService: MediaService,
    private userManagementService: UserManagementService
  ) {}

  ngOnInit(): void {
    this.updateColumns();
    this.loadSelectedOrganization();
    this.fetchLinkedEvents();
    this.fetchInterventionDetail();
    this.fetchUsersAndAuditHistory();
    this.setSubscriptionToRefreshList();

    // Subscribe to history updates
    this.interventionService.historyUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.fetchUsersAndAuditHistory();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadSelectedOrganization(): void {
    const storedOrganization = localStorage.getItem('selectedOrganization');
    if (storedOrganization) {
      this.selectedOrganization = JSON.parse(storedOrganization);
    }
  }

  setSubscriptionToRefreshList(): void {
    this.dialogEventService.statusSelectionChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        setTimeout(() => this.fetchLinkedEvents(), 200);
      });
  }

  cancelCoachingScript(): void {
    this.coachingScriptContents = this.coachingScriptOriginalContents;
  }

  fetchInterventionDetail(): void {
    this.userId = this.dataRow.assignee.toLowerCase();
    this.interventionService.getIntervention(this.dataRow.id).subscribe({
      next: (res) => {
        this.interventionDetail = res[0];
        this.coachingScriptContents = this.interventionDetail.coachingScript;
        this.coachingScriptOriginalContents = this.coachingScriptContents;
      },
      error: (error) => {
        console.log(error);
      },
    });
  }

  fetchLinkedEvents(fromUnlink?: boolean): void {
    const params = {
      interventionId: this.interventionId,
    };
    this.isLoading = true;
    this.eventService.getLinkedEventsToIntervention(params).subscribe({
      next: (res) => {
        if (res[0]?.events) {
          this.linkedEventList = res[0].events;
          this.fetchCoachingSamples();
        } else {
          this.linkedEventList = [];
        }
        if (fromUnlink && !this.linkedEventList.length) {
          this.setStatusToOpen();
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        console.log(error);
      },
    });
  }

  setStatusToOpen(): void {
    this.dataRow.status = StatusType.OPEN;
    const completed = false;
    const request = {
      id: this.dataRow?.id,
      status: this.dataRow?.status,
      completed,
      lastModifiedBy: this.dataRow?.lastModifiedBy,
      organizationId: environment.organizationId,
    };
    this.interventionService.upsertIntervention(request).subscribe({
      next: () => {
        this.interventionService.announceSelectionChange('update');
        this.toasterService.showSuccess(`The Status was updated successfully`);
      },
      error: (error) => console.error(error),
    });
  }

  getEventTypes(): string {
    const uniqueEventTypes = new Set();

    this.linkedEventList.forEach((event) => {
      if (event.eventType) {
        uniqueEventTypes.add(event.eventType);
      }
    });

    return Array.from(uniqueEventTypes).join(',');
  }

  fetchCoachingSamples(): void {
    const eventTypes = this.getEventTypes();
    const params = {
      eventTypes,
      organizationId: this.selectedOrganization.organizationId,
    };
    this.interventionService.getCoachingSamples(params).subscribe({
      next: (res) => {
        if (res[0]?.samples) {
          this.coachingStatistics = res[0].samples.filter(
            (sample: any) =>
              sample.sampleType === 'statistics' || sample.sampleType === 'statistic'
          );
          this.coachingExamples = res[0].samples.filter(
            (sample: any) => sample.sampleType === 'example'
          );
        }
      },
      error: (error) => {
        console.log(error);
      },
    });
  }

  openModalExamples(): void {
    const dialogConfig = {
      width: '800px',
      height: '600px',
      data: {
        title: 'Coaching Script Examples',
        content: this.coachingExamples,
      },
      id: 'coaching-samples',
    };

    this.dialog.open(DialogCoachingSamplesComponent, dialogConfig);
  }

  openModalStatistics(): void {
    const dialogConfig = {
      width: '800px',
      height: '600px',
      data: {
        title: 'Coaching Script Statistics',
        content: this.coachingStatistics,
      },
      id: 'coaching-samples',
    };

    this.dialog.open(DialogCoachingSamplesComponent, dialogConfig);
  }

  onNavClick(elementId: string): void {
    this.dialogEventService.onNavClick(elementId);
  }

  isRowSelected(id: string): boolean {
    return id === this.selectedRow;
  }

  onRowClick(data: any): void {
    this.flyoutService.handleDialogsOfType(FlyoutType.EVENT);
    this.selectedRow = data?.id;
    data.driverName = this.dataRow.driverName;
    data.driverId = this.driverId;
    data.terminal = this.dataRow.terminal;
    data.type = data.eventType;
    data.id = data.eventId;

    const isMobile = window.innerWidth <= MOBILE_WIDTH;

    setTimeout(() => {
      const dialogWidth = calculateDialogWidth(
        this.flyoutService.getFlyoutOffsetBasedOnIndex()
      );

      const config = isMobile
        ? {
            panelClass: 'full-screen-dialog',
            width: '100vw',
            height: '100vh',
            maxWidth: '100vw',
            maxHeight: '100vh',
            autoFocus: false,
            data: { ...data },
          }
        : {
            data: { ...data, showInfo: true, uniqueIdentifier: FlyoutType.EVENT },
            position: {
              right: '0',
              top: '70px',
            },
            width: dialogWidth,
            minWidth: dialogWidth,
            panelClass: ['dialog-event', 'animate-slide-in-left'],
            autoFocus: false,
          };

      this.dialog.open(DialogEventComponent, config);
    }, 100);
  }

  ngAfterViewInit(): void {
    this.attachScrollListener();
  }

  private attachScrollListener(): void {
    const articleElement = this.scrollContainer.nativeElement;
    articleElement.addEventListener('scroll', () => {
      this.onContainerScroll();
    });
  }

  private onContainerScroll(): void {
    let closestSection = null;
    let minDistance = Infinity;

    this.sections.forEach((section) => {
      const element = this.scrollContainer.nativeElement.querySelector(`#${section.id}`);
      if (element) {
        const rect = element.getBoundingClientRect();
        const distance = Math.abs(
          rect.top - this.scrollContainer.nativeElement.getBoundingClientRect().top
        );

        if (distance < minDistance) {
          minDistance = distance;
          closestSection = section.id;
        }
      }
    });

    if (closestSection !== null) {
      this.sectionSelected = closestSection;
    }
  }

  onEditorContentChange(newcoachingScriptContents: string) {
    this.changesAmount = this.changesAmount + 1;
    this.coachingScriptContents = newcoachingScriptContents;
    if (this.changesAmount > 1) {
      this.showSaveButton = true;
    }
    if (this.coachingScriptContents === this.coachingScriptOriginalContents) {
      this.showSaveButton = false;
    }
  }

  saveCoachingScript(): void {
    this.showSaveButton = false;
    const request = {
      id: this.dataRow?.id,
      lastModifiedBy: this.dataRow?.lastModifiedBy,
      coachingScript: this.coachingScriptContents,
      organizationId: environment.organizationId,
    };
    this.interventionService.upsertIntervention(request).subscribe({
      next: () => {
        this.coachingScriptOriginalContents = this.coachingScriptContents;
        this.toasterService.showSuccess(`The Coaching Script was updated successfully`);
        this.interventionService.announceHistoryUpdate();
        this.saveComplete.emit();
      },
      error: (error) => console.error(error),
    });
  }

  onUnlinkIntervention(intervention: any) {
    const data = {
      title: 'Unlink Intervention',
      text: 'Are you sure you want to unlink the intervention?',
      yesButtonText: 'Unlink',
      noButtonText: 'Cancel',
      noWarn: true,
    };
    this.dialog
      .open(DialogConfirmComponent, { data, width: '500px' })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.unlinkIntervention(intervention);
        }
      });
  }

  getUserIdFromLocalStorage(): string {
    let userId = '';
    const storedData = localStorage.getItem('userData');
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      userId = parsedData.userInfo.objectId;
    }
    return userId;
  }

  unlinkIntervention(intervention: any): void {
    const relationshipRequest = {
      id: intervention.id,
      eventId: intervention.eventId,
      interventionId: intervention.interventionId,
      userId: this.getUserIdFromLocalStorage(),
      deleted: true,
      organizationId: this.selectedOrganization?.organizationId,
    };

    this.interventionService
      .relationshipEventIntervention(relationshipRequest)
      .subscribe({
        next: () => {
          this.fetchLinkedEvents(true);
          this.interventionService.announceSelectionChange('update');
        },
        error: (relationshipError) => {
          console.error(relationshipError);
        },
      });
  }

  canLinkEventstoInterventions(): boolean {
    const userId = localStorage.getItem('objectId');
    return (
      (this.permissionService.hasPermission('WRITE', 'All Interventions') &&
        this.permissionService.hasPermission('WRITE', 'Events')) ||
      (this.permissionService.hasPermission('WRITE', 'My Interventions') &&
        this.userId === userId &&
        this.permissionService.hasPermission('WRITE', 'Events'))
    );
  }

  canViewInterventions(): boolean {
    return (
      this.permissionService.hasPermission('READ', 'All Interventions') ||
      this.permissionService.hasPermission('WRITE', 'All Interventions')
    );
  }

  canViewInterventionNotes(): boolean {
    return this.permissionService.hasPermission('READ', 'Intervention Notes');
  }

  canEditInterventions(): boolean {
    const userId = localStorage.getItem('objectId');
    return (
      this.permissionService.hasPermission('WRITE', 'All Interventions') ||
      (this.permissionService.hasPermission('WRITE', 'My Interventions') &&
        this.userId === userId)
    );
  }

  updateColumns(): void {
    this.linkedEventColumns = ['type', 'criteria', 'severity', 'date', 'status'];

    if (this.canLinkEventstoInterventions()) {
      this.linkedEventColumns.push('menu');
    }
  }

  fetchUsersAndAuditHistory(): void {
    this.isLoadingAudit = true;
    const params = {
      type: AssigneeUsersType.INTERVENTIONS,
      organizationId: this.selectedOrganization?.organizationId,
    };

    this.userManagementService
      .getUsers(params)
      .pipe(
        catchError((error) => {
          console.log('Error loading users:', error);
          return of(null);
        }),
        switchMap((response) => {
          if (response && response.length > 0) {
            this.users = response[0].userResults || [];
          }

          return this.loadAuditHistory();
        })
      )
      .subscribe();
  }

  private loadAuditHistory(): Observable<any> {
    if (!this.dataRow?.id) return of(null);

    return this.interventionService.getInterventionAudit(this.dataRow.id).pipe(
      map((res) => {
        if (res && res.length > 0 && res[0].interventionAuditSummaries) {
          this.interventionAuditHistory = res[0].interventionAuditSummaries;

          // Update displayed history immediately
          this.updateDisplayedAuditHistory();

          // Process user IDs and load images asynchronously
          this.interventionAuditHistory.forEach((item) => {
            if (item.changedBy !== 'System Generated') {
              const matchingUser = this.users.find(
                (user) => `${user.firstName} ${user.lastName}` === item.changedBy
              );
              if (matchingUser) {
                item.changedById = matchingUser.userId;
              }
            }

            if (item.fieldChanged === 'assignee') {
              if (item.oldValue) {
                item.oldValueId = item.oldValue;
                this.loadAssigneeImage(item.oldValue);
              }
              if (item.newValue) {
                item.newValueId = item.newValue;
                this.loadAssigneeImage(item.newValue);
              }
            }
          });
        } else {
          this.interventionAuditHistory = [];
          this.displayedAuditHistory = [];
        }
        this.isLoadingAudit = false;
        return res;
      }),
      catchError((error) => {
        console.error('Error fetching intervention audit history:', error);
        this.isLoadingAudit = false;
        return of(null);
      })
    );
  }

  private loadAssigneeImage(userId: string): void {
    if (!userId || this.assigneeImages[userId] !== undefined) return;

    this.assigneeImages[userId] = null;

    this.mediaService
      .downloadFile('userProfileImage', userId, 'profile_image')
      .subscribe({
        next: (blob: Blob) => {
          const imageUrl = URL.createObjectURL(blob);
          this.assigneeImages[userId] = imageUrl;
        },
        error: (error) => {
          if (error.status !== 404) {
            console.error(`Failed to load image for assignee ${userId}:`, error);
          }
          this.assigneeImages[userId] = null;
        },
      });
  }

  getAssigneeById(userId: string): any {
    if (!userId) return null;
    return this.users.find((user: any) => user.userId === userId) || { userId };
  }

  getAssigneeImage(userId: string): Observable<string | null> {
    return this.mediaService
      .downloadFile('userProfileImage', userId, 'profile_image')
      .pipe(
        map((blob: Blob) => {
          const imageUrl = URL.createObjectURL(blob);
          return imageUrl;
        }),
        catchError((error) => {
          console.error(`Failed to load image for user ${userId}:`, error);
          return of(null);
        })
      );
  }

  updateDisplayedAuditHistory(): void {
    if (this.showAllHistory) {
      this.displayedAuditHistory = [...this.interventionAuditHistory];
    } else {
      this.displayedAuditHistory = this.interventionAuditHistory.slice(
        0,
        this.maxAuditHistory
      );
    }
    this.displayedAuditHistory = [...this.displayedAuditHistory];
  }

  toggleAuditHistoryView(): void {
    this.showAllHistory = !this.showAllHistory;
    this.updateDisplayedAuditHistory();
  }

  formatFieldName(field: string): string {
    if (!field) return '';

    return field
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  getUserName(userId: string): string {
    if (!userId) return '';

    const user = this.users.find((user: any) => user.userId === userId);
    if (user) {
      return `${user.firstName} ${user.lastName}`;
    }

    return userId;
  }

  getStatusIcon(status: string): string {
    const statusObj = this.statuses.find(
      (s) => s.viewValue.toLowerCase() === status.toLowerCase()
    );
    return statusObj ? statusObj.icon : 'fa-solid fa-info-circle icon-default-size';
  }

  getOrganizationName(organizationId: string): string {
    const storedData = localStorage.getItem('userData');
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      const organization = parsedData?.permissions?.organizationPermissions?.find(
        (org: any) => org.organizationId === organizationId
      );
      return organization
        ? `${organizationId} (${organization.organizationName})`
        : organizationId;
    }
    return organizationId;
  }

  onSaveAndCompleteClicked(): void {
    this.saveAndCompleteClicked.emit();
  }

  shouldShowSaveAndCompleteButton(): boolean {
    // Only show the button for intervention notes in coaching details
    // and when the status is not already "Completed"
    if (
      this.entityType !== 'intervention' ||
      this.dataRow?.status?.toLowerCase() === CoachingStatusType.COMPLETED.toLowerCase()
    ) {
      return false;
    }

    // User must have WRITE permission for Intervention Notes
    const hasInterventionNotesWrite = this.permissionService.hasPermission(
      'WRITE',
      'Intervention Notes'
    );
    if (!hasInterventionNotesWrite) {
      return false;
    }

    // User must also have WRITE permission for either All Interventions or My Interventions (if they are the assignee)
    const userId = localStorage.getItem('objectId');
    const hasAllInterventionsWrite = this.permissionService.hasPermission(
      'WRITE',
      'All Interventions'
    );
    const hasMyInterventionsWrite =
      this.permissionService.hasPermission('WRITE', 'My Interventions') &&
      this.userId === userId;

    return hasAllInterventionsWrite || hasMyInterventionsWrite;
  }
}
